/**
 * First-party analytics tracker (PMS-API /analytics/track). Records the website funnel and Ask
 * Jade chat steps in our own database, where they can be joined to chats, leads and bookings
 * (GA4 / Meta keep getting their events from analytics.js).
 *
 * Anonymous by design: a random visitor id (localStorage) and session id (30 minutes idle), plus
 * the customer token when logged in. Never sends names, phone numbers or emails.
 *
 * Staff: open the site once with ?jc_internal=1 to stop tracking in that browser (?jc_internal=0
 * turns it back on).
 *
 * Every call is fire-and-forget: tracking never throws, never blocks a page and never shows errors.
 */

const { REACT_APP_API_URL, REACT_APP_FIRST_PARTY_TRACKING } = process.env;

const VISITOR_KEY = 'jadecaps_vid';
const SESSION_KEY = 'jadecaps_session';
const FIRST_TOUCH_KEY = 'jadecaps_first_touch';
const INTERNAL_KEY = 'jadecaps_internal';
const AI_CHAT_KEY = 'jadecaps_ai_chat';

const SESSION_IDLE_MS = 30 * 60 * 1000;
// Same window the API uses to credit a booking to an AI chat (analytics.events.js).
const AI_ATTRIBUTION_MS = 14 * 24 * 60 * 60 * 1000;
const FLUSH_DELAY_MS = 3000;
const MAX_QUEUE = 20;
const CLICK_IDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'];

let queue = [];
let flushTimer = null;
let lastPagePath = '';
let memoryStore = {};

function storage(kind) {
  try {
    const s = kind === 'session' ? window.sessionStorage : window.localStorage;
    const probe = '__jc_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

function read(key, kind = 'local') {
  try {
    const s = storage(kind);
    const raw = s ? s.getItem(key) : memoryStore[key];
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function write(key, value, kind = 'local') {
  try {
    const raw = JSON.stringify(value);
    const s = storage(kind);
    if (s) s.setItem(key, raw);
    else memoryStore[key] = raw;
  } catch {
    /* storage full or blocked — this page view only */
  }
}

function newId() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** ?jc_internal=1 / 0 marks this browser as staff (not tracked) or not. */
function applyInternalFlag(search) {
  try {
    const flag = new URLSearchParams(search || '').get('jc_internal');
    if (flag === '1') write(INTERNAL_KEY, true);
    if (flag === '0') write(INTERNAL_KEY, false);
  } catch {
    /* ignore */
  }
}

export function isTrackingEnabled() {
  if (typeof window === 'undefined' || !REACT_APP_API_URL) return false;
  if (String(REACT_APP_FIRST_PARTY_TRACKING || '').toLowerCase() === 'false') return false;
  if (window.navigator?.webdriver) return false;
  return read(INTERNAL_KEY) !== true;
}

function deviceType() {
  const ua = String(window.navigator?.userAgent || '');
  if (/iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(ua)) return 'tablet';
  if (/Mobi|iPhone|iPod|Android|Opera Mini|IEMobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

/** Where this visit came from: utm tags, ad click id, external referrer, landing page. */
function currentTouch() {
  const params = new URLSearchParams(window.location.search || '');
  let referrer = '';
  try {
    if (document.referrer && new URL(document.referrer).host !== window.location.host) referrer = document.referrer;
  } catch {
    /* ignore */
  }
  return {
    utmSource: params.get('utm_source') || '',
    utmMedium: params.get('utm_medium') || '',
    utmCampaign: params.get('utm_campaign') || '',
    clickId: CLICK_IDS.find((id) => params.get(id)) || '',
    referrer: referrer.slice(0, 500),
    landingPath: window.location.pathname,
    device: deviceType(),
    at: new Date().toISOString(),
  };
}

const hasCampaign = (t) => Boolean(t && (t.utmSource || t.clickId));

/** The current session, starting a new one after 30 idle minutes or when a new campaign lands. */
function currentSession() {
  const now = Date.now();
  let session = read(SESSION_KEY);
  const touch = currentTouch();
  const newCampaign = hasCampaign(touch) && session && (session.touch?.utmSource !== touch.utmSource || session.touch?.utmCampaign !== touch.utmCampaign || session.touch?.clickId !== touch.clickId);
  if (!session?.id || now - (session.lastAt || 0) > SESSION_IDLE_MS || newCampaign) {
    session = { id: newId(), startedAt: now, lastAt: now, touch };
    if (!read(FIRST_TOUCH_KEY)) write(FIRST_TOUCH_KEY, touch);
  }
  session.lastAt = now;
  write(SESSION_KEY, session);
  return session;
}

function visitorId() {
  let id = read(VISITOR_KEY);
  if (!id) {
    id = newId();
    write(VISITOR_KEY, id);
  }
  return id;
}

function customerToken() {
  try {
    const token = window.localStorage.getItem('customerToken');
    return token && token !== 'null' && token !== 'undefined' ? token : null;
  } catch {
    return null;
  }
}

function send(batch, { keepalive = false } = {}) {
  if (!batch.length) return;
  const session = currentSession();
  const { touch } = session;
  const body = {
    visitorId: visitorId(),
    sessionId: session.id,
    session: {
      utmSource: touch?.utmSource || '',
      utmMedium: touch?.utmMedium || '',
      utmCampaign: touch?.utmCampaign || '',
      clickId: touch?.clickId || '',
      referrer: touch?.referrer || '',
      landingPath: touch?.landingPath || '',
      device: touch?.device || deviceType(),
    },
    events: batch,
  };
  const headers = { 'Content-Type': 'application/json' };
  const token = customerToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  try {
    fetch(`${REACT_APP_API_URL}/analytics/track`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive,
      credentials: 'omit',
    }).catch(() => {});
  } catch {
    /* ignore */
  }
}

export function flushTracking(options) {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  const batch = queue;
  queue = [];
  send(batch, options);
}

/**
 * Record one step. ids: { propertyId, conversationId, bookingId }; everything else goes in props
 * (keep it small and never personal: numbers, flags, short labels).
 */
export function track(event, { propertyId, conversationId, bookingId, ...props } = {}) {
  try {
    if (!isTrackingEnabled() || !event) return;
    queue.push({
      event,
      at: Date.now(),
      path: window.location.pathname,
      propertyId: Number(propertyId) || null,
      conversationId: Number(conversationId) || null,
      bookingId: Number(bookingId) || null,
      props,
    });
    if (queue.length >= MAX_QUEUE) flushTracking();
    else if (!flushTimer) flushTimer = setTimeout(flushTracking, FLUSH_DELAY_MS);
  } catch {
    /* never break the page */
  }
}

/**
 * Route change: a page view when the path changes. Query-only changes (dates, filters, sort) are
 * the same page and are tracked as their own steps.
 */
export function trackPage(pathname, search) {
  applyInternalFlag(search);
  if (!pathname || pathname === lastPagePath) return;
  lastPagePath = pathname;
  const match = String(pathname || '').match(/^\/property\/(\d+)/);
  track('page_view', match ? { propertyId: Number(match[1]) } : {});
}

/** The guest chatted with Ask Jade (chat id), so a booking from this browser can credit it. */
export function rememberAiChat(conversationId) {
  if (!Number(conversationId)) return;
  write(AI_CHAT_KEY, { conversationId: Number(conversationId), at: Date.now() });
}

/** Sent with a booking: which visit, session and AI chat led to it, and where the guest came from. */
export function getTrackingContext() {
  try {
    if (!isTrackingEnabled()) return null;
    const session = currentSession();
    const chat = read(AI_CHAT_KEY);
    const recentChat = chat && Date.now() - Number(chat.at || 0) <= AI_ATTRIBUTION_MS ? chat.conversationId : null;
    return {
      visitorId: visitorId(),
      sessionId: session.id,
      aiConversationId: recentChat || null,
      attribution: { first: read(FIRST_TOUCH_KEY) || session.touch, last: session.touch },
    };
  } catch {
    return null;
  }
}

if (typeof window !== 'undefined') {
  applyInternalFlag(window.location.search);
  // Send what's queued when the tab is hidden or closed.
  window.addEventListener('pagehide', () => flushTracking({ keepalive: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTracking({ keepalive: true });
  });
}

/** Tests only. */
export function resetTrackerForTests() {
  queue = [];
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  lastPagePath = '';
  memoryStore = {};
}
